import { MouseEventHandler, ReactNode } from 'react'

import clsx from 'clsx'

import { ChildProps } from '@/common/util/props'

import IconButton from '@/components/view/atoms/button/IconButton'
import TextButton from '@/components/view/atoms/button/TextButton'
import Icon, { IconColor, IconType } from '@/components/view/atoms/icon/Icon'
import Text, { TextColor } from '@/components/view/atoms/text/Text'

export const MessageType = {
  SUCCESS: 'success',
  CRITICAL: 'critical',
  WARNING: 'warning',
  INFO: 'info',
  BRAND: 'brand'
} as const

export type MessageType = (typeof MessageType)[keyof typeof MessageType]

interface Props extends ChildProps {
  type: MessageType
  onClickLink?: MouseEventHandler<HTMLButtonElement>
  linkText?: string
  fillIn?: boolean
  onClickClose?: MouseEventHandler<HTMLButtonElement>
  toast?: boolean
  rightNode?: ReactNode
  iconType?: IconType
  center?: boolean
}

const MessageBar = ({
  children,
  type,
  onClickLink,
  linkText,
  fillIn = false,
  onClickClose,
  toast = false,
  rightNode,
  iconType,
  center = false
}: Props) => {
  const iconTypeVal =
    iconType ??
    (type == MessageType.CRITICAL
      ? IconType.ERROR
      : type == MessageType.WARNING
        ? IconType.WARNING
        : type == MessageType.SUCCESS
          ? IconType.CIRCLE_CHECK
          : type == MessageType.BRAND
            ? IconType.CART
            : IconType.ERROR)

  const bgClass = fillIn
    ? type == MessageType.SUCCESS
      ? 'bg-surface-success'
      : type == MessageType.CRITICAL
        ? 'bg-surface-critical'
        : type == MessageType.WARNING
          ? 'bg-surface-caution border border-caution'
          : type == MessageType.BRAND
            ? 'bg-fill-primary border-brand-primary'
            : 'bg-surface-info border border-info'
    : 'bg-fill-primary'

  const iconColor = !fillIn
    ? type == MessageType.WARNING
      ? IconColor.CAUTION
      : type == MessageType.INFO
        ? IconColor.INFO
        : IconColor.ON_COLOR
    : type == MessageType.SUCCESS
      ? IconColor.ON_COLOR
      : type == MessageType.CRITICAL
        ? IconColor.CRITICAL
        : type == MessageType.WARNING
          ? IconColor.CAUTION
          : IconColor.ON_COLOR

  const iconBgColor = iconType
    ? ''
    : type == MessageType.SUCCESS
      ? 'bg-fill-success'
      : type == MessageType.CRITICAL
        ? 'bg-fill-critical'
        : type == MessageType.WARNING
          ? 'bg-fill-caution'
          : type == MessageType.BRAND
            ? 'bg-fill-primary-brand'
            : 'bg-fill-info'

  const textColor =
    type == MessageType.CRITICAL
      ? TextColor.CRITICAL
      : fillIn
        ? type == MessageType.SUCCESS
          ? TextColor.SUCCESS
          : type == MessageType.WARNING
            ? TextColor.CAUTION
            : TextColor.INFO
        : TextColor.PRIMARY

  const Inner = ({ children }: ChildProps) => {
    return (
      <>
        <span
          className={clsx(
            'grid h-6 min-h-[24px] w-6 min-w-[24px] shrink place-content-center rounded',
            iconBgColor
          )}
        >
          <Icon
            color={iconColor}
            type={iconTypeVal}
            line={type == MessageType.SUCCESS ? false : !iconType}
          />
        </span>
        <Text
          lineHeight={150}
          color={textColor}
          weight={type == MessageType.CRITICAL ? 'bold' : 'normal'}
        >
          {children}
        </Text>
        {linkText && onClickLink && (
          <TextButton onClick={onClickLink} nowrap>
            {linkText}
          </TextButton>
        )}
        {(rightNode || onClickClose) && (
          <div className='ml-auto flex items-center justify-end gap-x-1'>
            {rightNode && <>{rightNode}</>}
            {onClickClose && (
              <span className={'ml-auto block'}>
                <IconButton type={IconType.CLOSE} color={'secondary'} onClick={onClickClose} />
              </span>
            )}
          </div>
        )}
      </>
    )
  }

  return (
    <div
      className={clsx(
        'flex gap-x-2 rounded-lg',
        center ? 'items-center' : 'items-start',
        bgClass,
        fillIn ? 'w-full p-2' : 'shadow-bevel-100 w-full px-1 py-1',
        toast && 'animate-toast w-fit translate-x-full'
      )}
    >
      {type == MessageType.BRAND ? (
        <div className={clsx('z-10 flex gap-x-2', center ? 'items-center' : 'items-start')}>
          <Inner>{children}</Inner>
        </div>
      ) : (
        <Inner>{children}</Inner>
      )}
    </div>
  )
}

export default MessageBar
